import { GridColumnState } from '@progress/kendo-react-grid';
import { orderColumn, reorderColumn, updateColumnsChildrenFields } from '@components-clean/core/grid/grid.helper';
import { GridColumnProps, ORDER_COLUMN_ID, REORDER_COLUMN_ID } from '@components-clean/core/grid/grid.interface';

const flattenColumnStates = (cols: GridColumnState[]): GridColumnState[] =>
    cols.flatMap((col) => [col, ...(col.children ? flattenColumnStates(col.children) : [])]);

export const updateColumnsFields = (
    columns: GridColumnProps[],
    rowReorder: boolean | undefined,
    orderIndexColumn: string | undefined,
    savedColumns?: GridColumnState[],
) => {
    const savedColumnsMap = new Map(flattenColumnStates(savedColumns ?? []).map((col) => [col.id, col]));
    const newCols = columns.map((column, index) => {
        const savedColumn = savedColumnsMap.get(column.id ?? '');
        return {
            ...column,
            locked: savedColumn?.locked ?? column.locked ?? column.lockedByDefault,
            show: savedColumn != null ? !savedColumn.hidden : (column.show ?? column.showByDefault ?? true),
            children: updateColumnsChildrenFields(column.children, savedColumnsMap),
            orderIndex: savedColumn?.orderIndex ?? column.orderIndex ?? index + 1,
        };
    });

    if (rowReorder) {
        newCols.unshift(
            {
                ...orderColumn,
                orderIndex: -1,
                field: orderIndexColumn,
                locked: false,
                show: true,
                showByDefault: true,
                children: undefined,
                customColumn: true,
                width: 75,
                id: ORDER_COLUMN_ID,
                lockedByDefault: false,
                filter: 'numeric',
            },
            {
                ...reorderColumn,
                orderIndex: -2,
                locked: false,
                show: false,
                showByDefault: false,
                children: undefined,
                customColumn: true,
                width: 50,
                id: REORDER_COLUMN_ID,
                lockedByDefault: false,
            },
        );
    }

    // Kendo doesn't reliably re-derive visual column order from a columnsState entry's `orderIndex`
    // alone — the array/declaration order of the rendered <GridColumn> elements matters too. Sort the
    // array itself to match, so applying a saved layout (or resetting to no saved layout) actually
    // rearranges the columns instead of just tagging them with the right orderIndex value.
    return newCols.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
};
